import { useTranslation } from 'react-i18next'
import { Receipt } from 'lucide-react'
import { DetailCard } from '@/admin/components/shared/detail-card'
import { formatCurrency } from '@/shared/lib/formatting'
import type { ConsumerSettlement } from '@/consumer/hooks/use-consumer-claims'

/**
 * WPS-138 #14 - the settlement summary the homeowner reviews before they
 * accept or dispute.
 *
 * Display rule (confirmed with the client): every line shows the product name
 * + colour (falling back to the line description); a voucher line then shows a
 * quantity only (no dollar figure), and every other line quantity + unit price +
 * line total; the priced lines sum to the settlement total shown at the foot
 * (voucher amounts are excluded). All amounts are formatted with the tenant's
 * own locale + currency. Purely a renderer of the API payload - no tenant
 * branching, no hardcoded copy beyond the seeded translations.
 */
export function SettlementSummaryCard({
  settlement,
  locale,
  currency,
}: {
  settlement: ConsumerSettlement
  locale: string
  currency: string
}) {
  const { t } = useTranslation()
  const money = (value: string) => formatCurrency(Number(value || 0), currency, locale)
  // Trim a trailing ".0000"/".00" so "3.0000" reads as "3" but "2.5" stays.
  const qty = (value: string) => {
    const n = Number(value || 0)
    return Number.isFinite(n) ? String(n) : value
  }

  return (
    <DetailCard
      title={t('claimDetail.settlementTitle', 'Settlement')}
      icon={<Receipt className="h-3.5 w-3.5" />}
      collapsible={false}
    >
      <div className="p-3">
        <p className="mb-3 text-[12px] text-[var(--ink-3)]">
          {t(
            'claimDetail.settlementSubtitle',
            'Please review the settlement below before you respond.',
          )}
        </p>
        <div className="flex flex-col gap-2">
          {settlement.line_items.map((item, index) => (
            <div
              key={`${item.product_name || item.description}-${index}`}
              className="flex items-start justify-between gap-3 border-b-[0.5px] border-[var(--rule)] pb-2 last:border-b-0 last:pb-0"
            >
              <div className="min-w-0 flex-1">
                {/* Every line leads with the product name and colour, falling
                    back to the line description; a voucher line then shows its
                    quantity only. */}
                <p className="text-[13px] text-[var(--ink)]">
                  {item.product_name || item.description}
                </p>
                {item.color && (
                  <p className="text-[11px] text-[var(--ink-3)]">{item.color}</p>
                )}
                <p className="text-[11px] text-[var(--ink-3)]">
                  {item.is_voucher
                    ? t('claimDetail.settlementVoucherQty', {
                        defaultValue: 'Voucher qty {{qty}}',
                        qty: qty(item.voucher_quantity ?? '0'),
                      })
                    : t('claimDetail.settlementQtyPrice', {
                        defaultValue: 'Qty {{qty}} at {{price}} each',
                        qty: qty(item.quantity),
                        price: money(item.unit_price),
                      })}
                </p>
              </div>
              {/* Voucher lines carry no dollar figure for the homeowner. */}
              {!item.is_voucher && (
                <p className="shrink-0 text-[13px] font-medium tabular-nums text-[var(--ink)]">
                  {money(item.extended_price)}
                </p>
              )}
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between border-t-[0.5px] border-[var(--rule)] pt-3">
          <p className="text-[12px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
            {t('claimDetail.settlementTotal', 'Settlement total')}
          </p>
          <p className="text-[15px] font-medium tabular-nums text-[var(--ink)]">
            {money(settlement.total)}
          </p>
        </div>
      </div>
    </DetailCard>
  )
}
